import React, { useEffect, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, Loader2 } from "lucide-react";

export const OwnerTransfer = ({ onDone }) => {
  const { user, setUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [target, setTarget] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users").then(({ data }) => setAdmins(data.filter(u => u.role === "admin" && u.id !== user?.id))).catch(() => {});
  }, [user?.id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/system/transfer-owner", { target_user_id: target, password, confirm });
      toast.success(`Ownership transferred to ${data.owner}. You remain an admin.`);
      setUser({ ...user, is_owner: false });
      onDone?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Transfer failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="p-5 space-y-4" data-testid="owner-transfer-form">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2"><Crown size={12} className="text-emerald-400"/>Transfer ownership</div>
      <p className="text-xs text-zinc-400 leading-relaxed">Hands the Owner role to another admin. They gain password rotation, admin deletes, assignments, freeze and system reset. You keep your admin account but lose those owner-only powers. This is logged and cannot be undone by you.</p>
      {admins.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-dashed border-white/10 p-4 text-center" data-testid="owner-transfer-no-admins">No other admin accounts exist. Create one on the Users page first.</div>
      ) : (
        <>
          <label className="block text-xs text-zinc-400">New owner
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1 w-full bg-transparent border border-white/20 px-3 py-2 text-sm outline-none" data-testid="owner-transfer-target">
              <option value="" className="bg-black">Select an admin…</option>
              {admins.map(a => <option key={a.id} value={a.id} className="bg-black">{a.name} — {a.email}</option>)}
            </select>
          </label>
          <label className="block text-xs text-zinc-400">Your password
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full bg-transparent border-b border-white/20 py-2 outline-none focus:border-emerald-400 text-sm" data-testid="owner-transfer-password"/>
          </label>
          <label className="block text-xs text-zinc-400">Type <span className="font-mono text-emerald-300">TRANSFER</span>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 w-full bg-transparent border-b border-white/20 py-2 outline-none focus:border-emerald-400 text-sm font-mono" data-testid="owner-transfer-confirm"/>
          </label>
          <button type="submit" disabled={busy || !target || !password || confirm !== "TRANSFER"}
            className="px-4 py-2 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400 disabled:opacity-40 flex items-center gap-2" data-testid="owner-transfer-submit">
            {busy ? <Loader2 size={13} className="animate-spin"/> : <Crown size={13}/>} Transfer ownership
          </button>
        </>
      )}
    </form>
  );
};
